import { keys } from "@prismicio/editor-support/Object"
import { some } from "@prismicio/editor-support/Set"
import { isDefined } from "@prismicio/editor-support/Type"
import {
  RichTextConfig,
  type RichTextNodeType,
  RichTextNodeTypeCodec
} from "@prismicio/types-internal/lib/customtypes"

import { getAllExtensions, Label, Paragraph } from "../extensions"
import {
  isListExtension,
  isTextExtension,
  type WidgetExtension
} from "./EditorExtension"

export function composeExtensions(config?: RichTextConfig): Extensions {
  if (!config || (!config?.single && !config?.multi)) return allExtensions()

  const nodeTypes = getNodeTypes(config)
  if (!nodeTypes.size) return allExtensions()

  const configExtensions = selectConfigExtensions(nodeTypes)
  const extensions = addEssentialExtensions(configExtensions, config)

  return {
    nodeTypes,
    extensions
  }
}

function getNodeTypes(config: RichTextConfig): Set<RichTextNodeType> {
  const extensionTypes = (config.single || config.multi || "").split(/\s*,\s*/)
  const nodeTypes = extensionTypes
    .map((type) => {
      const decoded = RichTextNodeTypeCodec.decode(type)
      return decoded._tag === "Right" ? decoded.right : undefined
    })
    .filter(isDefined)

  return new Set(nodeTypes)
}

function selectConfigExtensions(nodeTypes: Set<RichTextNodeType>) {
  const allExtensions = getAllExtensions(nodeTypes)

  return allExtensions.filter((extension) =>
    some(nodeTypes, (nodeType) =>
      extension.richTextNodeTypes.includes(nodeType)
    )
  )
}

function addEssentialExtensions(
  configExtensions: WidgetExtension[],
  config: RichTextConfig
) {
  const set = new Set(configExtensions)

  // ListExtension requires Paragraph as a dependency.
  // Note: It probably shouldn't because ul > li is a cleaner markup than ul > li > p
  // but the current implementation requires it.
  if (some(set, isListExtension)) set.add(Paragraph)

  // If we still don't have any Text based extensions after all this, add Paragraph,
  // which is the best/neutral text node extension.
  if (!some(set, isTextExtension)) set.add(Paragraph)

  // If config contains labels add Label extension.
  if (config?.labels?.length) set.add(Label)

  return [...set]
}

interface Extensions {
  extensions: WidgetExtension[]
  nodeTypes: Set<RichTextNodeType>
}

function allExtensions(): Extensions {
  return {
    extensions: getAllExtensions(),
    nodeTypes: new Set(keys(RichTextNodeTypeCodec.keys))
  }
}
